/**
 * Account-scoped à la carte bids (`alaCarteBids` collection).
 * Workflow fields mirror catalog items but each bid is tied to one account.
 */

import { PRICING_STATUS, departmentToPostedTeam } from './alaCarteCatalog';

export { PRICING_STATUS, departmentToPostedTeam };

/**
 * Internal bids are BBA's own work, priced by a team but not tied to a client or lead.
 * Same `'internal'` id CRM Tasks uses for internal tasks; there is no accounts doc behind it.
 */
export const INTERNAL_BID_ACCOUNT_ID = 'internal';
export const INTERNAL_BID_COMPANY = { name: 'BBA', logoUrl: '/bba-emblem.svg' };

export function isInternalBid(bid) {
  return bid?.accountId === INTERNAL_BID_ACCOUNT_ID || bid?.accountType === 'Internal';
}

/** Pill label for a bid's `accountType`. */
export function bidAccountTypeLabel(accountType) {
  if (accountType === 'Internal') return 'Internal';
  return accountType === 'Clients' ? 'Client' : 'Lead';
}

function defaultDeliverableRows(data) {
  const existing = Array.isArray(data.deliverableRows) ? data.deliverableRows : [];
  if (existing.length > 0) return existing;
  return [];
}

function defaultTeamInput(data) {
  const ti = data.teamInput && typeof data.teamInput === 'object' ? data.teamInput : {};
  const rows = Array.isArray(ti.deliverableRows) ? ti.deliverableRows : [];
  return {
    scope: typeof ti.scope === 'string' ? ti.scope : '',
    internalCostEstimate:
      ti.internalCostEstimate === '' || ti.internalCostEstimate === undefined || ti.internalCostEstimate === null
        ? ''
        : ti.internalCostEstimate,
    deliverableRows: rows,
  };
}

function defaultInternalOnly(data) {
  const io = data.internalOnly && typeof data.internalOnly === 'object' ? data.internalOnly : {};
  return {
    notes: typeof io.notes === 'string' ? io.notes : '',
  };
}

/**
 * One line of a bid package. `catalogItemId` is empty for custom lines typed by an
 * exec — a package can mix catalog services with one-off work that isn't in the catalog.
 */
function normalizePackageItem(raw) {
  const o = raw && typeof raw === 'object' ? raw : {};
  const quantity = Number(o.quantity);
  return {
    catalogItemId: typeof o.catalogItemId === 'string' ? o.catalogItemId : '',
    name: typeof o.name === 'string' ? o.name.trim() : '',
    notes: typeof o.notes === 'string' ? o.notes.trim() : '',
    icon: o.icon || '📦',
    department: typeof o.department === 'string' ? o.department : '',
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    custom: !o.catalogItemId,
  };
}

/**
 * @param {unknown} raw
 * @returns {ReturnType<typeof normalizePackageItem>[]}
 */
export function normalizePackageItems(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizePackageItem).filter((item) => item.name);
}

/**
 * Package lines mirrored into the shared `deliverableRows` shape so the team workspace,
 * the client portal, and the signed contract render a package with no special-casing.
 */
export function packageItemsToDeliverableRows(items) {
  return normalizePackageItems(items).map((item) => ({
    quantity: String(item.quantity),
    description: item.notes ? `${item.name} — ${item.notes}` : item.name,
  }));
}

function normalizeBidNegotiation(raw) {
  const o = raw && typeof raw === 'object' ? raw : {};
  return {
    counterClientPrice:
      o.counterClientPrice !== undefined && o.counterClientPrice !== null ? String(o.counterClientPrice) : '',
    counterInternalCost:
      o.counterInternalCost !== undefined && o.counterInternalCost !== null ? String(o.counterInternalCost) : '',
    counterNote: typeof o.counterNote === 'string' ? o.counterNote : '',
    execDecisionNote: typeof o.execDecisionNote === 'string' ? o.execDecisionNote : '',
  };
}

export const BID_CONTRACT_STATUS = {
  none: '',
  sent: 'sent',
  clientSigned: 'client_signed',
  completed: 'completed',
  declined: 'declined',
  voided: 'voided',
};

const BID_CONTRACT_VALUES = new Set(['sent', 'client_signed', 'completed', 'declined', 'voided']);

function normalizeBidContract(raw) {
  const o = raw && typeof raw === 'object' ? raw : {};
  return {
    envelopeId: typeof o.envelopeId === 'string' ? o.envelopeId : '',
    status: BID_CONTRACT_VALUES.has(o.status) ? o.status : '',
    sentAt: o.sentAt ?? null,
    signedAt: o.signedAt ?? null,
    signerName: typeof o.signerName === 'string' ? o.signerName : '',
    signerEmail: typeof o.signerEmail === 'string' ? o.signerEmail : '',
    price: o.price !== undefined && o.price !== null ? String(o.price) : '',
    url: typeof o.url === 'string' ? o.url : '',
    fileName: typeof o.fileName === 'string' ? o.fileName : '',
  };
}

export const BID_CONTRACT_STATUS_LABELS = {
  [BID_CONTRACT_STATUS.sent]: 'Contract sent',
  [BID_CONTRACT_STATUS.clientSigned]: 'Client signed',
  [BID_CONTRACT_STATUS.completed]: 'Contract signed',
  [BID_CONTRACT_STATUS.declined]: 'Signing declined',
  [BID_CONTRACT_STATUS.voided]: 'Contract voided',
};

export const CLIENT_RESPONSE_STATUS = {
  none: '',
  accepted: 'accepted',
  declined: 'declined',
  countered: 'countered',
};

const CLIENT_RESPONSE_VALUES = new Set(['accepted', 'declined', 'countered']);

function normalizeClientResponse(raw) {
  const o = raw && typeof raw === 'object' ? raw : {};
  return {
    status: CLIENT_RESPONSE_VALUES.has(o.status) ? o.status : '',
    counterPrice: o.counterPrice !== undefined && o.counterPrice !== null ? String(o.counterPrice) : '',
    note: typeof o.note === 'string' ? o.note : '',
    respondedAt: o.respondedAt ?? null,
    respondedByUid: typeof o.respondedByUid === 'string' ? o.respondedByUid : '',
    respondedByName: typeof o.respondedByName === 'string' ? o.respondedByName : '',
  };
}

/**
 * @param {string} id
 * @param {Record<string, unknown>} data
 */
export function normalizeAlaCarteBid(id, data) {
  const pricingType = data.pricingType || 'caseByCase';
  const deliverableRows = defaultDeliverableRows(data);

  return {
    ...data,
    id,
    accountId: typeof data.accountId === 'string' ? data.accountId : '',
    accountType: ['Clients', 'Leads', 'Internal'].includes(data.accountType) ? data.accountType : '',
    companyName: typeof data.companyName === 'string' ? data.companyName : '',
    catalogItemId: typeof data.catalogItemId === 'string' ? data.catalogItemId : '',
    department: typeof data.department === 'string' ? data.department : 'General',
    pricingType,
    name: typeof data.name === 'string' ? data.name : '',
    description: typeof data.description === 'string' ? data.description : '',
    icon: data.icon || '📦',
    scope: typeof data.scope === 'string' ? data.scope : '',
    // A package bundles several services (catalog and/or custom) under one price,
    // one scope, and one contract. `catalogItemId` is empty on package bids.
    isPackage: data.isPackage === true,
    packageItems: normalizePackageItems(data.packageItems),
    deliverableRows,
    standardPricing: {
      cost: 0,
      unit: 'item',
      stripeProductId: '',
      ...(data.standardPricing || {}),
    },
    internalOnly: defaultInternalOnly(data),
    teamInput: defaultTeamInput(data),
    pricingStatus: data.pricingStatus || PRICING_STATUS.draft,
    postedToTeam: data.postedToTeam ?? null,
    postedAt: data.postedAt ?? null,
    postedByUid: data.postedByUid ?? null,
    teamSubmittedAt: data.teamSubmittedAt ?? null,
    internalCost:
      data.internalCost === '' || data.internalCost === undefined || data.internalCost === null
        ? ''
        : data.internalCost,
    markupPercent:
      data.markupPercent === '' || data.markupPercent === undefined || data.markupPercent === null
        ? ''
        : data.markupPercent,
    clientListPrice:
      data.clientListPrice === '' || data.clientListPrice === undefined || data.clientListPrice === null
        ? ''
        : data.clientListPrice,
    bidNegotiation: normalizeBidNegotiation(data.bidNegotiation),
    clientResponse: normalizeClientResponse(data.clientResponse),
    contract: normalizeBidContract(data.contract),
    assignedPartner:
      data.assignedPartner && typeof data.assignedPartner === 'object' ? data.assignedPartner : null,
    source: data.source === 'portal' ? 'portal' : data.source === 'crm' ? 'crm' : '',
    sourceRequestId: typeof data.sourceRequestId === 'string' ? data.sourceRequestId : '',
    createdByUid: typeof data.createdByUid === 'string' ? data.createdByUid : '',
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
  };
}

export function mapAlaCarteBidSnapshotDocs(docs) {
  return docs.map((d) => normalizeAlaCarteBid(d.id, d.data()));
}

export const BID_STATUS_LABELS = {
  [PRICING_STATUS.draft]: 'Draft',
  [PRICING_STATUS.awaiting_team]: 'Open',
  [PRICING_STATUS.team_submitted]: 'Submitted',
  [PRICING_STATUS.exec_ready]: 'Exec review',
  [PRICING_STATUS.published]: 'Published',
};

export function accountOverviewPath(bid) {
  if (!bid?.accountId || isInternalBid(bid)) return null;
  if (bid.accountType === 'Clients') return `/crm/client/${bid.accountId}`;
  return `/crm/lead/${bid.accountId}`;
}

export function teamQueuePathForBid(bid) {
  const slugMap = { Marketing: 'marketing', Finance: 'finance', HR: 'hr' };
  const slug = slugMap[bid?.department];
  if (!slug || !bid?.id) return null;
  return `/crm/${slug}-alacarte-queue/${bid.id}`;
}

export const CLIENT_RESPONSE_LABELS = {
  [CLIENT_RESPONSE_STATUS.accepted]: 'Accepted',
  [CLIENT_RESPONSE_STATUS.declined]: 'Declined',
  [CLIENT_RESPONSE_STATUS.countered]: 'Counter sent',
};

/**
 * Client-safe projection of a normalized bid — strips internal cost, margin,
 * team estimates, and exec/team negotiation. Portal surfaces must only render this.
 */
export function sanitizeBidForClient(bid) {
  if (!bid) return null;
  return {
    id: bid.id,
    // Catalog reference only — lets program builders match a published price to the
    // deliverable card it was quoted for. Carries no internal pricing.
    catalogItemId: bid.catalogItemId,
    name: bid.name,
    description: bid.description,
    icon: bid.icon,
    department: bid.department,
    pricingType: bid.pricingType,
    isPackage: bid.isPackage === true,
    packageItems: normalizePackageItems(bid.packageItems),
    scope: bid.scope,
    deliverableRows: Array.isArray(bid.deliverableRows) ? bid.deliverableRows : [],
    clientListPrice: bid.clientListPrice,
    quantity: bid.quantity,
    pricingStatus: bid.pricingStatus,
    clientResponse: normalizeClientResponse(bid.clientResponse),
    // Signing progress only — the signed PDF stays internal (account documents).
    contract: (() => {
      const c = normalizeBidContract(bid.contract);
      return { envelopeId: c.envelopeId, status: c.status, sentAt: c.sentAt, signedAt: c.signedAt };
    })(),
    createdAt: bid.createdAt ?? null,
    updatedAt: bid.updatedAt ?? null,
  };
}
